'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const DEMO_ACCOUNTS = [
  { role: 'Fish Farmer', email: 'farmer@fiscmap.com', password: 'demo1234', color: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
  { role: 'Student', email: 'student@fiscmap.com', password: 'demo1234', color: 'bg-blue-50 border-blue-300 text-blue-700' },
  { role: 'Entrepreneur', email: 'entrepreneur@fiscmap.com', password: 'demo1234', color: 'bg-orange-50 border-orange-300 text-orange-700' },
  { role: 'Technician', email: 'technician@fiscmap.com', password: 'demo1234', color: 'bg-purple-50 border-purple-300 text-purple-700' },
];

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/personalized-dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError('');
    setLoading(true);
    try {
      await signIn(demoEmail, demoPassword);
      router.push('/personalized-dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Demo login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-secondary to-ocean-teal flex-col justify-between p-12 text-white">
        <Link href="/" className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <span className="text-xl font-headline font-bold">FISCMAP</span>
            <span className="text-xs text-white/70 block">Portal</span>
          </div>
        </Link>

        <div>
          <h1 className="text-4xl font-headline font-bold mb-4 leading-tight">
            Welcome back to<br />FISCMAP Portal
          </h1>
          <p className="text-white/80 text-lg mb-8">
            Access your personalized fisheries dashboard, track course progress, and connect with market opportunities.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: 'AcademicCapIcon', label: '50+ Courses', sub: 'Expert-led training' },
              { icon: 'UserGroupIcon', label: '20,000+ Learners', sub: 'Active community' },
              { icon: 'TrophyIcon', label: '28,000+ Certs', sub: 'Issued certificates' },
              { icon: 'GlobeAltIcon', label: 'Market Access', sub: 'Direct buyer connect' },
            ].map((item) => (
              <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Icon name={item.icon as any} size={24} variant="solid" className="text-accent mb-2" />
                <p className="font-semibold text-sm">{item.label}</p>
                <p className="text-white/60 text-xs">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/50 text-sm">© 2026 FISCMAP Portal. All rights reserved.</p>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="white"/>
              </svg>
            </div>
            <span className="text-xl font-headline font-bold text-foreground">FISCMAP Portal</span>
          </div>

          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">Sign in</h2>
          <p className="text-muted-foreground mb-8">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="text-primary font-semibold hover:underline">
              Create one free
            </Link>
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <Icon name="ExclamationCircleIcon" size={18} variant="solid" className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} variant="outline" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-headline font-semibold hover:bg-primary/90 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Demo Accounts */}
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">Try demo accounts</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((demo) => (
                <button
                  key={demo.role}
                  onClick={() => handleDemoLogin(demo.email, demo.password)}
                  disabled={loading}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all hover:shadow-sm disabled:opacity-50 ${demo.color}`}
                >
                  {demo.role}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">All demo accounts use password: <span className="font-mono font-semibold">demo1234</span></p>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-8">
            By signing in, you agree to our{' '}
            <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}
